import React, { useMemo, useRef, useState } from "react";
import propTypes from "prop-types";
import OutsideClickHandler from "react-outside-click-handler";
import ArrowDown from "../../../assets/icons/arrow-down.svg";
import ArrowUp from "../../../assets/icons/arrow-up.svg";

const isPrimitive = option =>
  typeof option === "string" || typeof option === "number";

const getOptionValue = (option, optionValue) =>
  isPrimitive(option) ? option : option?.[optionValue];

const getOptionLabel = (option, optionLabel) =>
  isPrimitive(option) ? String(option) : option?.[optionLabel];

function GenericMultiSelect({
  options = [],
  value = [],
  onChange,
  optionValue = "id",
  optionLabel = "name",
  placeholder = "Select...",
  width = "w-[22rem]",
  searchable = true,
  disabled = false,
  error = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const selectedOptions = useMemo(() => {
    if (!value?.length) return [];
    if (isPrimitive(value[0])) {
      return (options || []).filter(option =>
        value.includes(getOptionValue(option, optionValue)),
      );
    }
    return value;
  }, [value, options, optionValue]);

  const selectedValues = useMemo(
    () => selectedOptions.map(option => getOptionValue(option, optionValue)),
    [selectedOptions, optionValue],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options || [];
    const query = search.toLowerCase();
    return (options || []).filter(option =>
      getOptionLabel(option, optionLabel)?.toLowerCase()?.includes(query),
    );
  }, [options, search, searchable, optionLabel]);

  const focusInput = () => {
    if (searchable && !disabled) {
      inputRef.current?.focus();
    }
  };

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    focusInput();
  };

  const closeMenu = () => {
    setOpen(false);
    setSearch("");
  };

  const handleSelect = option => {
    const nextValue = getOptionValue(option, optionValue);
    const isSelected = selectedValues.includes(nextValue);

    if (isSelected) {
      handleRemove(option);
      return;
    }

    if (value?.length && isPrimitive(value[0])) {
      onChange?.([...value, nextValue]);
    } else if (!value?.length && isPrimitive(option)) {
      onChange?.([nextValue]);
    } else {
      onChange?.([...selectedOptions, option]);
    }
    setSearch("");
    focusInput();
  };

  const handleRemove = optionToRemove => {
    const removeValue = getOptionValue(optionToRemove, optionValue);
    if (value?.length && isPrimitive(value[0])) {
      onChange?.(value.filter(item => item !== removeValue));
      return;
    }
    onChange?.(
      selectedOptions.filter(
        option => getOptionValue(option, optionValue) !== removeValue,
      ),
    );
  };

  const handleKeyDown = e => {
    if (e.key === "Backspace" && !search && selectedOptions.length) {
      handleRemove(selectedOptions.at(-1));
    }
    if (e.key === "Escape") {
      closeMenu();
    }
  };

  const borderClass = error
    ? "border-red-300"
    : open
      ? "border-primary-200"
      : "border-secondary-300";

  const firstSelected = selectedOptions[0];
  const remainingCount = Math.max(selectedOptions.length - 1, 0);

  return (
    <OutsideClickHandler onOutsideClick={closeMenu}>
      <div className={`relative ${width}`}>
        <div
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
          className={`min-h-[3rem] bg-white rounded-2xl border-2 pl-3 pr-10 py-2 w-full cursor-text flex items-center gap-2 overflow-hidden ${borderClass} ${
            disabled ? "opacity-60 cursor-not-allowed" : ""
          } ${className}`}
          onClick={openMenu}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openMenu();
            }
          }}>
          {firstSelected && (
            <div className="flex items-center gap-2 min-w-0 shrink">
              <span className="inline-flex items-center gap-1.5 max-w-[9rem] bg-secondary-200 text-secondary-600 text-sm font-medium rounded-full pl-3 pr-2 py-1">
                <span className="truncate capitalize">
                  {getOptionLabel(firstSelected, optionLabel)}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    className="flex items-center justify-center w-4 h-4 rounded-full text-secondary-800 hover:bg-secondary-300 hover:text-secondary-600 text-xs leading-none shrink-0"
                    onClick={e => {
                      e.stopPropagation();
                      handleRemove(firstSelected);
                    }}
                    aria-label={`Remove ${getOptionLabel(
                      firstSelected,
                      optionLabel,
                    )}`}>
                    &#10005;
                  </button>
                )}
              </span>
              {remainingCount >= 1 && (
                <span
                  className="inline-flex items-center shrink-0 bg-secondary-200 text-secondary-600 text-sm font-semibold rounded-full px-2.5 py-1"
                  title={selectedOptions
                    .slice(1)
                    .map(option => getOptionLabel(option, optionLabel))
                    .join(", ")}>
                  +{remainingCount}
                </span>
              )}
            </div>
          )}

          {searchable ? (
            <input
              ref={inputRef}
              type="text"
              className="flex-1 min-w-[4rem] bg-transparent outline-none font-semibold text-sm placeholder-bold"
              placeholder={selectedOptions.length ? "" : placeholder}
              value={search}
              disabled={disabled}
              onChange={e => {
                setSearch(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                if (!disabled) setOpen(true);
              }}
              onKeyDown={handleKeyDown}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            !selectedOptions.length && (
              <span className="text-sm text-secondary-800 font-semibold">
                {placeholder}
              </span>
            )
          )}

          <button
            type="button"
            disabled={disabled}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 p-1"
            onClick={e => {
              e.stopPropagation();
              if (open) closeMenu();
              else openMenu();
            }}
            aria-label={open ? "Close options" : "Open options"}>
            <img src={open ? ArrowUp : ArrowDown} alt="" />
          </button>
        </div>

        {open && (
          <ul className="absolute z-[1000] top-[calc(100%+0.5rem)] left-0 w-full max-h-48 overflow-y-auto bg-white rounded-2xl border border-secondary-200 shadow-lg py-2 scrollbar-thin scrollbar-thumb-secondary-700 scrollbar-thumb-rounded">
            {filteredOptions.length === 0 ? (
              <li className="font-ibm text-sm text-secondary-800 py-2 px-4">
                No options found
              </li>
            ) : (
              filteredOptions.map(option => {
                const key = getOptionValue(option, optionValue);
                const isSelected = selectedValues.includes(key);
                return (
                  <li
                    key={String(key)}
                    role="option"
                    tabIndex={0}
                    aria-selected={isSelected}
                    className={`font-ibm text-sm font-medium py-2 px-4 cursor-pointer flex items-center justify-between capitalize ${
                      isSelected
                        ? "bg-primary-400 text-secondary-600"
                        : "text-secondary-600 hover:bg-secondary-200"
                    }`}
                    onClick={() => handleSelect(option)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect(option);
                      }
                    }}>
                    <span>{getOptionLabel(option, optionLabel)}</span>
                    {isSelected && (
                      <span className="text-primary-200 text-xs font-semibold">
                        ✓
                      </span>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
}

GenericMultiSelect.propTypes = {
  options: propTypes.array,
  value: propTypes.array,
  onChange: propTypes.func,
  optionValue: propTypes.string,
  optionLabel: propTypes.string,
  placeholder: propTypes.string,
  width: propTypes.string,
  searchable: propTypes.bool,
  disabled: propTypes.bool,
  error: propTypes.bool,
  className: propTypes.string,
};

export default GenericMultiSelect;
